import React, { useEffect, useState } from "react";
import { CountryBadge } from "@/components/admin/CountryBadge";
import { api, formatError, SERVICES, CUSTOM_QUOTE_SERVICES, CLIENT_TYPES, ADDONS_SERVICES, addonsFor, PARTIAL_SERVICE } from "@/lib/api";
import { RIVIERA_CITIES } from "@/lib/riviera";
import PartialTasksPicker, { hasPartialSelection, partialTasksPayload } from "@/components/PartialTasksPicker";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Jobs from "@/pages/admin/Jobs";
import { toast } from "sonner";
import { format } from "date-fns";
import { CheckCircle2, Send, FileDown, Mail, Phone, MapPin, Calendar, Clock, XCircle, Plus, User, Home, Building2, Pencil, Sparkles, Tag, Globe } from "lucide-react";

const STATUS_OPTIONS = [
  "Pending Confirmation",
  "Quoted",
  "Confirmed",
  "Lost",
  "Cancelled",
];

const STATUS_COLOR = {
  "Pending Confirmation": "bg-amber-100 text-amber-800",
  "Confirmed": "bg-emerald-100 text-emerald-800",
  "Lost": "bg-slate-200 text-slate-600",
  "Cancelled": "bg-rose-100 text-rose-800",
};

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("active");
  const [country, setCountry] = useState("all");
  const [opened, setOpened] = useState(null);
  const [notes, setNotes] = useState("");
  const [working, setWorking] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [adjustTarget, setAdjustTarget] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/bookings");
      setBookings(r.data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const byCountry = (b) => country === "all" || (b.country || "NL").toUpperCase() === country;
  const statusFiltered =
    filter === "all"
      ? bookings
      : filter === "active"
        ? bookings.filter((b) => !["Cancelled", "Lost"].includes(b.status))
        : bookings.filter((b) => b.status === filter);
  const filtered = statusFiltered.filter(byCountry);

  const updateStatus = async (booking, newStatus, extra = {}) => {
    setWorking(true);
    try {
      await api.patch(`/bookings/${booking.id}`, { status: newStatus, internal_notes: notes || undefined, ...extra });
      if (newStatus === "Confirmed") {
        toast.success("Booking confirmed — invoice email sent to customer.");
      } else if (newStatus === "Cancelled") {
        toast.success(`Booking cancelled — removed from active list.`);
      } else {
        toast.success(`Booking marked as ${newStatus}.`);
      }
      setOpened(null);
      setCancelTarget(null);
      setNotes("");
      await load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setWorking(false);
    }
  };

  const downloadInvoice = async (invoice_no) => {
    try {
      const token = localStorage.getItem("trustline_token");
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      const resp = await fetch(`${apiBase}/invoices/${invoice_no}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch { toast.error("Could not load invoice"); }
  };

  const resendInvoice = async (booking) => {
    if (!window.confirm(`Re-send the invoice email to ${booking.customer_email}?`)) return;
    setWorking(true);
    try {
      const r = await api.post(`/admin/bookings/${booking.id}/resend-invoice`);
      toast.success(`Invoice ${r.data.invoice_no} re-sent to ${r.data.to}.`);
      await load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setWorking(false);
    }
  };

  const counts = {
    "Pending Confirmation": bookings.filter((b) => b.status === "Pending Confirmation").length,
    "Confirmed": bookings.filter((b) => b.status === "Confirmed").length,
    "Lost": bookings.filter((b) => b.status === "Lost").length,
    "Cancelled": bookings.filter((b) => b.status === "Cancelled").length,
  };

  return (
    <div className="space-y-6" data-testid="admin-bookings-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1B3A6B]">Bookings</h1>
          <p className="text-sm text-slate-500 mt-1">Review online booking requests. Confirm to auto-create a job + email the customer their invoice.</p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-[#5BA83F] hover:bg-[#4A8A33] text-white rounded-full"
          data-testid="admin-new-booking-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New booking
        </Button>
      </div>

      <Tabs defaultValue="requests" className="w-full">
        <TabsList className="bg-slate-100" data-testid="bookings-tabs">
          <TabsTrigger value="requests" data-testid="bookings-tab-requests">Booking requests</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="bookings-tab-jobs">Jobs</TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="space-y-6 mt-5">
          <div className="flex items-center justify-end gap-2">
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="w-[180px]" data-testid="bookings-filter-country"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="bookings-filter-country-all">All countries</SelectItem>
                <SelectItem value="NL" data-testid="bookings-filter-country-nl">Netherlands (NL)</SelectItem>
                <SelectItem value="FR" data-testid="bookings-filter-country-fr">France (FR)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[220px]" data-testid="bookings-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active ({bookings.filter((b) => !["Cancelled","Lost"].includes(b.status)).length})</SelectItem>
                <SelectItem value="all">All bookings ({bookings.length})</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{s} ({counts[s] || 0})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {STATUS_OPTIONS.map((s) => (
          <button key={s} type="button" onClick={() => setFilter(s)}
            className={`text-left p-4 rounded-xl border transition ${filter === s ? "border-[#1B3A6B] bg-slate-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
            data-testid={`bookings-stat-${s.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="text-xs uppercase tracking-wide font-semibold text-slate-500">{s}</div>
            <div className="font-heading text-2xl font-bold text-[#1B3A6B] mt-1">{counts[s] || 0}</div>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl ring-1 ring-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Service</th>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No bookings in this view.</td></tr>
            )}
            {filtered.map((b) => {
              let when = b.date;
              try { when = format(new Date(b.date), "MMM d, yyyy"); } catch { /* ignore */ }
              return (
                <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`booking-row-${b.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#0F172A] flex items-center gap-1.5">{b.customer_name} <CountryBadge country={b.country} testid={`booking-country-${b.id}`} /></div>
                    <div className="text-xs text-slate-500">{b.customer_email}</div>
                    {b.invoice_no && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold bg-slate-100 text-slate-600 ring-1 ring-slate-200 rounded px-1.5 py-0.5" data-testid={`booking-invoice-no-${b.id}`}>
                        <FileDown className="w-3 h-3" /> {b.invoice_no}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#0F172A]">{b.service_type}</div>
                    <div className="text-xs text-slate-500">{b.city} · {b.sqm} m²</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{when}</div>
                    <div className="text-xs text-slate-500">{b.start_time}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-[#1B3A6B]">€{b.amount?.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <Badge className={`${STATUS_COLOR[b.status] || "bg-slate-100 text-slate-600"} border-0`}>{b.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => { setOpened(b); setNotes(b.internal_notes || ""); }} data-testid={`booking-open-${b.id}`}>
                      Review
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
        </TabsContent>

        <TabsContent value="jobs" className="mt-5" data-testid="bookings-jobs-tab-content">
          <Jobs />
        </TabsContent>
      </Tabs>

      <Dialog open={!!opened} onOpenChange={(o) => !o && setOpened(null)}>
        <DialogContent className="max-w-2xl">
          {opened && (
            <>
              <DialogHeader>
                <DialogTitle>Booking · {opened.service_type}</DialogTitle>
                <DialogDescription>Reference {opened.id.slice(0, 8).toUpperCase()} · Submitted {opened.created_at?.slice(0, 10)}</DialogDescription>
              </DialogHeader>

              {opened.invoice_no && (
                <div className="rounded-xl bg-emerald-50 ring-1 ring-emerald-200 p-3 flex items-start gap-3" data-testid="booking-invoice-banner">
                  <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                    <FileDown className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs uppercase tracking-wide font-semibold text-emerald-700">Invoice issued</div>
                    <div className="font-heading font-bold text-[#1B3A6B] truncate">{opened.invoice_no}</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      Sent to <b>{opened.customer_email}</b>
                      {opened.invoice_last_resent_at && (
                        <> · resent {opened.invoice_last_resent_at.slice(0, 16).replace("T", " ")}</>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <Field icon={<Mail className="w-4 h-4" />} label="Customer">
                  <div className="font-medium">{opened.customer_name}</div>
                  <div className="text-xs text-slate-500">{opened.customer_email}</div>
                </Field>
                <Field icon={<Phone className="w-4 h-4" />} label="Phone">
                  <a href={`https://wa.me/${(opened.customer_phone || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-[#1B3A6B] font-medium underline">{opened.customer_phone}</a>
                </Field>
                <Field icon={<MapPin className="w-4 h-4" />} label="Address">
                  {opened.address}, {opened.city}
                </Field>
                <Field icon={<Calendar className="w-4 h-4" />} label="Date / time">
                  {opened.date} · {opened.start_time}
                </Field>
                <Field icon={<Clock className="w-4 h-4" />} label="Frequency / size">
                  {opened.frequency} · {opened.sqm} m² · {opened.floors} floor(s) {opened.is_commercial ? "· Commercial" : ""}
                </Field>
                <Field icon={<Clock className="w-4 h-4" />} label="Estimated duration">
                  <span className="font-semibold text-[#1B3A6B]">
                    {formatDuration(opened.price?.billable_hours ?? opened.price?.hours ?? opened.estimated_hours)}
                  </span>
                  {opened.price?.addons_minutes > 0 && (
                    <span className="text-xs text-slate-500"> · incl. {opened.price.addons_minutes}min add-ons</span>
                  )}
                  {opened.price?.partial_tasks?.length > 0 && (
                    <div className="text-xs text-slate-600 mt-0.5" data-testid="booking-detail-partial-tasks">
                      Areas: {opened.price.partial_tasks.map((x) => `${x.label}${x.qty > 1 ? ` ×${x.qty}` : ""}`).join(", ")}
                    </div>
                  )}
                </Field>
                <Field icon={<CheckCircle2 className="w-4 h-4" />} label="Total">
                  <span className="font-bold text-[#1B3A6B]">€{opened.amount?.toFixed(2)}</span>
                  {opened.price_manually_adjusted && (
                    <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold bg-amber-100 text-amber-800 ring-1 ring-amber-200 rounded px-1.5 py-0.5">Adjusted</span>
                  )}
                </Field>
              </div>

              {(opened.access_instructions || opened.special_requests) && (
                <div className="mt-2 space-y-2">
                  {opened.access_instructions && <div className="text-xs"><b>Access:</b> {opened.access_instructions}</div>}
                  {opened.special_requests && <div className="text-xs"><b>Special requests:</b> {opened.special_requests}</div>}
                </div>
              )}

              <div className="mt-3">
                <Label>Internal notes</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" rows={2} data-testid="booking-notes" />
              </div>

              <div className="mt-2 text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded-md p-3">
                <b>Confirming</b> will create a job, generate the official PDF invoice, email it to <b>{opened.customer_email}</b> from <b>info@trustlineservices.nl</b>, and log the email in the customer&apos;s account.
              </div>

              <DialogFooter className="flex flex-wrap gap-2 sm:gap-2">
                {opened.status === "Pending Confirmation" && (
                  <Button
                    variant="outline"
                    onClick={() => { setEditDraft(opened); setOpened(null); }}
                    data-testid="booking-edit-draft-btn"
                  >
                    <Pencil className="w-4 h-4 mr-1" /> Edit draft
                  </Button>
                )}
                {(opened.status === "Confirmed" || opened.status === "Payment Pending" || opened.status === "Quoted") && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => { setRescheduleTarget(opened); }}
                      data-testid="booking-reschedule-btn"
                    >
                      <Calendar className="w-4 h-4 mr-1" /> Reschedule
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setAdjustTarget(opened); }}
                      data-testid="booking-adjust-price-btn"
                    >
                      <Tag className="w-4 h-4 mr-1" /> Adjust price
                    </Button>
                  </>
                )}
                {opened.invoice_no && (
                  <Button variant="outline" onClick={() => downloadInvoice(opened.invoice_no)} data-testid="booking-download-invoice">
                    <FileDown className="w-4 h-4 mr-1" /> Invoice PDF
                  </Button>
                )}
                {opened.invoice_no && (
                  <Button variant="outline" onClick={() => resendInvoice(opened)} disabled={working} data-testid="booking-resend-invoice">
                    <Send className="w-4 h-4 mr-1" /> {working ? "Sending…" : "Resend invoice"}
                  </Button>
                )}
                {opened.status !== "Confirmed" && (
                  <Button onClick={() => updateStatus(opened, "Confirmed")} disabled={working} className="bg-[#5BA83F] hover:bg-[#4A8A33] text-white" data-testid="booking-confirm-btn">
                    <Send className="w-4 h-4 mr-1" /> Confirm & email invoice
                  </Button>
                )}
                {opened.status !== "Lost" && (
                  <Button variant="outline" onClick={() => updateStatus(opened, "Lost")} disabled={working} data-testid="booking-lost-btn">
                    <XCircle className="w-4 h-4 mr-1" /> Mark Lost
                  </Button>
                )}
                {opened.status !== "Cancelled" && (
                  <Button variant="outline" onClick={() => setCancelTarget(opened)} disabled={working} className="text-rose-600 border-rose-200" data-testid="booking-cancel-btn">
                    Cancel
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AdminCreateBookingDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async () => {
          setCreateOpen(false);
          await load();
        }}
      />

      <AdminCreateBookingDialog
        open={!!editDraft}
        initial={editDraft}
        onClose={() => setEditDraft(null)}
        onCreated={async () => {
          setEditDraft(null);
          await load();
        }}
      />

      <CancelBookingDialog
        booking={cancelTarget}
        working={working}
        onClose={() => setCancelTarget(null)}
        onConfirm={(reason, reasonOther) =>
          updateStatus(cancelTarget, "Cancelled", {
            cancellation_reason: reason,
            cancellation_reason_other: reasonOther,
          })
        }
      />

      <RescheduleBookingDialog
        booking={rescheduleTarget}
        onClose={() => setRescheduleTarget(null)}
        onDone={async () => { setRescheduleTarget(null); await load(); setOpened(null); }}
      />

      <AdjustPriceDialog
        booking={adjustTarget}
        onClose={() => setAdjustTarget(null)}
        onDone={async () => { setAdjustTarget(null); await load(); setOpened(null); }}
      />
    </div>
  );
}

function Field({ icon, label, children }) {
  return (
    <div className="bg-slate-50 rounded-lg px-3 py-2.5 ring-1 ring-slate-200">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500 font-semibold">
        {icon} {label}
      </div>
      <div className="mt-1 text-slate-800">{children}</div>
    </div>
  );
}

// ---------------- Admin create booking ----------------
const FREQUENCIES = [
  { value: "one-time", label: "One-time" },
  { value: "weekly", label: "Weekly (−15%)" },
  { value: "bi-weekly", label: "Bi-weekly (−10%)" },
  { value: "monthly", label: "Monthly (−5%)" },
];
const FURNISHED_OPTIONS = ["Furnished", "Semi-furnished", "Empty"];

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  // Customer
  customer_name: "",
  customer_phone: "",
  customer_email: "",
  client_type: "Residential / Private",
  // Address
  address: "",
  city: "",
  postal_code: "",
  // Service
  service_type: "Deep Cleaning",
  sqm: "",
  floors: 1,
  bedrooms: 1,
  bathrooms: 1,
  furnished: "Furnished",
  employees: 0,
  frequency: "one-time",
  // When
  date: today(),
  start_time: "09:00",
  // Notes
  special_requests: "",
  access_instructions: "",
  // Admin options
  auto_confirm: false,
  // Admin discount
  discount_type: "none", // none | code | percent | fixed
  discount_code: "",
  discount_pct: "",
  discount_amount: "",
  discount_reason: "",
  // Advanced pricing
  complexity_pct: "",            // empty → backend default 1.25
  hourly_rate_override: "",
  billable_hours_override: "",
  fixed_quote_excl_vat: "",
  // Add-ons (Regular/Maintenance + Deep Cleaning only)
  addons: [],
  country: "NL",
  partial_tasks: {},
};

function AdminCreateBookingDialog({ open, onClose, onCreated, initial }) {
  const isEditDraft = !!initial;
  const buildInitial = () => {
    if (!initial) return emptyForm;
    return {
      customer_name: initial.customer_name || "",
      customer_phone: initial.customer_phone || "",
      customer_email: initial.customer_email || "",
      client_type: initial.client_type || "Residential / Private",
      address: initial.address || "",
      city: initial.city || "",
      postal_code: initial.postal_code || "",
      service_type: initial.service_type || "Regular/Maintenance Cleaning",
      sqm: initial.sqm ?? "",
      floors: initial.floors ?? 1,
      bedrooms: initial.bedrooms ?? 1,
      bathrooms: initial.bathrooms ?? 1,
      furnished: initial.furnished || "Furnished",
      employees: initial.employees ?? 0,
      frequency: initial.frequency || "one-time",
      date: initial.date || today(),
      start_time: initial.start_time || "09:00",
      special_requests: initial.special_requests || "",
      access_instructions: initial.access_instructions || "",
      auto_confirm: false,
      discount_type: initial.price?.discount?.code ? "code" : "none",
      discount_code: initial.price?.discount?.code || "",
      discount_pct: "", discount_amount: "", discount_reason: "",
      complexity_pct: "", hourly_rate_override: "", billable_hours_override: "", fixed_quote_excl_vat: "",
      addons: Array.isArray(initial.addons) ? initial.addons : (initial.price?.addons || []).map((a) => a.key),
      country: (initial.country || "NL").toUpperCase(),
      partial_tasks: initial.partial_tasks || {},
    };
  };

  const [form, setForm] = useState(buildInitial);
  const [submitting, setSubmitting] = useState(false);
  const [submitMode, setSubmitMode] = useState(null);
  const [pricePreview, setPricePreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(buildInitial());
      setPricePreview(null);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open, initial?.id]);

  const isCustomQuote = CUSTOM_QUOTE_SERVICES.includes(form.service_type);
  const isCommercial = form.client_type === "Commercial / B2B";
  const isPartial = form.service_type === PARTIAL_SERVICE;
  const isFR = form.country === "FR";

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const buildPricingPayload = () => {
    const p = {
      service_type: form.service_type,
      sqm: Number(form.sqm) || 0,
      floors: Number(form.floors) || 1,
      frequency: form.frequency,
      is_commercial: isCommercial,
      employees: Number(form.employees) || 0,
      client_type: form.client_type,
      country: form.country,
    };
    if (isPartial) p.partial_tasks = partialTasksPayload(form.partial_tasks);
    if (form.complexity_pct !== "" && form.complexity_pct !== null)
      p.complexity_pct = Number(form.complexity_pct);
    if (form.hourly_rate_override !== "" && Number(form.hourly_rate_override) > 0)
      p.hourly_rate_override = Number(form.hourly_rate_override);
    if (form.billable_hours_override !== "" && Number(form.billable_hours_override) > 0)
      p.billable_hours_override = Number(form.billable_hours_override);
    if (form.fixed_quote_excl_vat !== "" && Number(form.fixed_quote_excl_vat) > 0)
      p.fixed_quote_excl_vat = Number(form.fixed_quote_excl_vat);
    if (form.discount_type === "code" && form.discount_code) p.discount_code = form.discount_code;
    if (form.discount_type === "percent" && form.discount_pct) p.discount_pct = Number(form.discount_pct);
    if (form.discount_type === "fixed" && form.discount_amount) p.discount_amount = Number(form.discount_amount);
    if (Array.isArray(form.addons) && form.addons.length && ADDONS_SERVICES.includes(form.service_type)) {
      p.addons = form.addons;
    }
    return p;
  };

  const previewPrice = async () => {
    if (isCustomQuote && !form.hourly_rate_override && !form.fixed_quote_excl_vat) {
      toast.error(`${form.service_type} requires a custom quote — enter an hourly rate or fixed quote below.`);
      return;
    }
    setPreviewing(true);
    try {
      const r = await api.post("/pricing/calculate", buildPricingPayload());
      setPricePreview(r.data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (isCustomQuote) {
      toast.error(`${form.service_type} requires a custom quote — please create a Lead instead.`);
      return;
    }
    if (isPartial && !hasPartialSelection(form.partial_tasks, form.addons)) {
      toast.error("Select at least one area or add-on for Custom / Partial Cleaning.");
      return;
    }
    // Basic validation
    const required = ["customer_name", "customer_phone", "customer_email", "address", "city", "service_type", "date", "start_time"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please fill in ${k.replace(/_/g, " ")}.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        sqm: Number(form.sqm) || 0,
        floors: Number(form.floors) || 1,
        bedrooms: Number(form.bedrooms) || 0,
        bathrooms: Number(form.bathrooms) || 0,
        employees: Number(form.employees) || 0,
        is_commercial: isCommercial,
        customer_email: String(form.customer_email).trim().toLowerCase(),
        partial_tasks: isPartial ? partialTasksPayload(form.partial_tasks) : undefined,
      };

      // Map UI-only discount/pricing controls to backend fields
      const pricingExtras = {};
      if (form.complexity_pct !== "" && form.complexity_pct !== null) pricingExtras.complexity_pct = Number(form.complexity_pct);
      if (form.hourly_rate_override !== "" && Number(form.hourly_rate_override) > 0) pricingExtras.hourly_rate_override = Number(form.hourly_rate_override);
      if (form.billable_hours_override !== "" && Number(form.billable_hours_override) > 0) pricingExtras.billable_hours_override = Number(form.billable_hours_override);
      if (form.fixed_quote_excl_vat !== "" && Number(form.fixed_quote_excl_vat) > 0) pricingExtras.fixed_quote_excl_vat = Number(form.fixed_quote_excl_vat);
      if (form.discount_type === "code" && form.discount_code) pricingExtras.discount_code = form.discount_code.trim().toUpperCase();
      if (form.discount_type === "percent" && form.discount_pct) pricingExtras.discount_pct = Number(form.discount_pct);
      if (form.discount_type === "fixed" && form.discount_amount) pricingExtras.discount_amount = Number(form.discount_amount);
      if (form.discount_type !== "none" && form.discount_reason) pricingExtras.discount_reason = form.discount_reason;
      if (Array.isArray(form.addons) && form.addons.length && ADDONS_SERVICES.includes(form.service_type)) {
        pricingExtras.addons = form.addons;
      }

      // Remove UI-only keys
      delete payload.discount_type;
      delete payload.discount_pct;
      delete payload.discount_amount;
      delete payload.complexity_pct;
      delete payload.hourly_rate_override;
      delete payload.billable_hours_override;
      delete payload.fixed_quote_excl_vat;
      delete payload.discount_reason;
      delete payload.addons;
      if (form.discount_type === "none") delete payload.discount_code;

      if (isEditDraft) {
        // Edit mode — strip create-only fields
        const { auto_confirm: _ac, ...editPayload } = payload;
        const r = await api.patch(`/admin/bookings/${initial.id}/draft`, { ...editPayload, ...pricingExtras });
        toast.success(`Draft updated (€${Number(r.data.amount).toFixed(2)}).`);
      } else {
        const mode = submitMode || "draft";
        const { auto_confirm: _ac, ...rest } = payload;
        const r = await api.post("/admin/bookings", { ...rest, ...pricingExtras, mode });
        const amount = Number(r.data.amount).toFixed(2);
        if (mode === "confirm") {
          toast.success(`Booking confirmed — invoice emailed to ${payload.customer_email} (€${amount}).`);
        } else if (mode === "quote") {
          toast.success(`Quote sent to ${payload.customer_email} (€${amount}).`);
        } else {
          toast.success(`Draft saved (€${amount}). Open it to confirm later.`);
        }
      }
      await onCreated?.();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSubmitting(false);
      setSubmitMode(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={isEditDraft ? "admin-edit-booking-dialog" : "admin-create-booking-dialog"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#5BA83F]" />
            {isEditDraft ? "Edit booking draft" : "New booking (admin)"}
          </DialogTitle>
          <DialogDescription>
            {isEditDraft
              ? "Edit any detail of this draft before confirming. Pricing is recalculated automatically when service, size or frequency change."
              : "Create a booking on behalf of a customer (e.g. phone / WhatsApp). If the customer email doesn't exist yet, an account is auto-created."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5">
          <section className="flex flex-wrap items-center gap-3" data-testid="cb-country-section">
            <SectionTitle icon={<Globe className="w-4 h-4" />}>Country / market</SectionTitle>
            <div className="inline-flex rounded-full bg-slate-100 p-0.5 ring-1 ring-slate-200 -mt-2" data-testid="cb-country-toggle">
              {[["NL", "Netherlands"], ["FR", "France"]].map(([c, l]) => (
                <button key={c} type="button" data-testid={`cb-country-${c.toLowerCase()}`}
                  onClick={() => { set({ country: c, city: c === "FR" ? RIVIERA_CITIES[0].name : "" }); setPricePreview(null); }}
                  className={`px-4 h-8 rounded-full text-xs font-semibold transition-colors ${form.country === c ? "bg-white text-[#1B3A6B] shadow-sm" : "text-slate-500 hover:text-[#1B3A6B]"}`}>{l}</button>
              ))}
            </div>
            {isFR && <span className="text-[11px] text-sky-700 -mt-2" data-testid="cb-fr-note">France prices &amp; VAT · Monaco allowed (admin only)</span>}
          </section>

          {/* Customer */}
          <section>
            <SectionTitle icon={<User className="w-4 h-4" />}>Customer</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-3">
              <Labeled label="Full name">
                <Input value={form.customer_name} onChange={(e) => set({ customer_name: e.target.value })} required data-testid="cb-customer-name" />
              </Labeled>
              <Labeled label="Phone (WhatsApp)">
                <Input value={form.customer_phone} onChange={(e) => set({ customer_phone: e.target.value })} placeholder="+31 6 ..." required data-testid="cb-customer-phone" />
              </Labeled>
              <Labeled label="Email">
                <Input type="email" value={form.customer_email} onChange={(e) => set({ customer_email: e.target.value })} required data-testid="cb-customer-email" />
              </Labeled>
              <Labeled label="Client type">
                <Select value={form.client_type} onValueChange={(v) => set({ client_type: v })}>
                  <SelectTrigger data-testid="cb-client-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CLIENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Labeled>
            </div>
          </section>

          {/* Address */}
          <section>
            <SectionTitle icon={<MapPin className="w-4 h-4" />}>Service address</SectionTitle>
            <div className="grid sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <Labeled label="Street + number">
                  <Input value={form.address} onChange={(e) => set({ address: e.target.value })} required data-testid="cb-address" />
                </Labeled>
              </div>
              <Labeled label="Postal code">
                <Input
                  value={form.postal_code}
                  onChange={(e) => set({ postal_code: e.target.value })}
                  placeholder="1011 AB"
                  data-testid="cb-postal-code"
                />
              </Labeled>
              <Labeled label="City">
                {isFR ? (
                  <Select value={form.city} onValueChange={(v) => set({ city: v })}>
                    <SelectTrigger data-testid="cb-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                    <SelectContent>
                      {RIVIERA_CITIES.map((c) => <SelectItem key={c.slug} value={c.name} data-testid={`cb-city-option-${c.slug}`}>{c.name}{c.inquiryOnly ? " · admin only" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={form.city}
                    onChange={(e) => set({ city: e.target.value })}
                    placeholder="Amsterdam"
                    required
                    data-testid="cb-city"
                  />
                )}
              </Labeled>
            </div>
          </section>

          {/* Service */}
          <section>
            <SectionTitle icon={isCommercial ? <Building2 className="w-4 h-4" /> : <Home className="w-4 h-4" />}>
              Service details
            </SectionTitle>
            <div className="grid sm:grid-cols-3 gap-3">
              <Labeled label="Service type">
                <Select value={form.service_type} onValueChange={(v) => { const ok = addonsFor(v).map((x) => x.key); set({ service_type: v, addons: (form.addons || []).filter((k) => ok.includes(k)) }); setPricePreview(null); }}>
                  <SelectTrigger data-testid="cb-service-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}{CUSTOM_QUOTE_SERVICES.includes(s) ? "  · quote only" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Labeled>
              <Labeled label="Frequency">
                <Select value={form.frequency} onValueChange={(v) => { set({ frequency: v }); setPricePreview(null); }}>
                  <SelectTrigger data-testid="cb-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Labeled>
              <Labeled label="Size (m²)">
                <Input
                  type="number"
                  min="0"
                  placeholder="ex: 200"
                  value={form.sqm}
                  onChange={(e) => { set({ sqm: e.target.value }); setPricePreview(null); }}
                  data-testid="cb-sqm"
                />
              </Labeled>

              <Labeled label="Floors / levels">
                <Input type="number" min="1" value={form.floors} onChange={(e) => { set({ floors: e.target.value }); setPricePreview(null); }} data-testid="cb-floors" />
              </Labeled>
              {!isCommercial ? (
                <>
                  <Labeled label="Bedrooms">
                    <Input type="number" min="0" value={form.bedrooms} onChange={(e) => set({ bedrooms: e.target.value })} data-testid="cb-bedrooms" />
                  </Labeled>
                  <Labeled label="Bathrooms">
                    <Input type="number" min="0" value={form.bathrooms} onChange={(e) => set({ bathrooms: e.target.value })} data-testid="cb-bathrooms" />
                  </Labeled>
                  <Labeled label="Furnished">
                    <Select value={form.furnished} onValueChange={(v) => set({ furnished: v })}>
                      <SelectTrigger data-testid="cb-furnished"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FURNISHED_OPTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Labeled>
                </>
              ) : (
                <Labeled label="Employees on-site">
                  <Input type="number" min="0" value={form.employees} onChange={(e) => set({ employees: e.target.value })} data-testid="cb-employees" />
                </Labeled>
              )}
            </div>

            {isPartial && (
              <div className="mt-4" data-testid="cb-partial-section">
                <div className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">Areas to clean (min. 2 hours)</div>
                <PartialTasksPicker value={form.partial_tasks} onChange={(v) => { set({ partial_tasks: v }); setPricePreview(null); }} testidPrefix="cb-partial" compact />
              </div>
            )}

            {isCustomQuote && (
              <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm px-3 py-2">
                <b>{form.service_type}</b> requires a custom quote — pricing isn&apos;t auto-calculated. Please create this as a Lead instead and quote the customer manually.
              </div>
            )}

            {!isCustomQuote && (
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <Button type="button" variant="outline" onClick={previewPrice} disabled={previewing} data-testid="cb-preview-price">
                  {previewing ? "Calculating…" : "Preview price"}
                </Button>
                {pricePreview && (
                  <div className="text-sm">
                    Estimate:{" "}
                    <b className="text-[#1B3A6B]">
                      €{Number(pricePreview.total || 0).toFixed(2)}
                    </b>{" "}
                    <span className="text-slate-500">
                      ({formatDuration(pricePreview.billable_hours || pricePreview.hours || 0)} · VAT {pricePreview.vat_pct ?? "—"}%)
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Add-ons (Regular/Maintenance + Deep Cleaning only) */}
            {ADDONS_SERVICES.includes(form.service_type) && (
              <div className="mt-4">
                <div className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">Add-ons (optional)</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {addonsFor(form.service_type).map((a) => {
                    const checked = form.addons.includes(a.key);
                    return (
                      <label
                        key={a.key}
                        className={`cursor-pointer text-xs rounded-lg px-3 py-2 ring-1 transition ${
                          checked
                            ? "bg-[#E8F5E0] ring-[#5BA83F] text-[#1F4D0F]"
                            : "bg-white ring-slate-200 text-slate-700 hover:ring-slate-300"
                        }`}
                        data-testid={`cb-addon-${a.key}`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...form.addons, a.key]
                              : form.addons.filter((k) => k !== a.key);
                            set({ addons: next });
                            setPricePreview(null);
                          }}
                        />
                        <div className="font-semibold">{a.label}</div>
                        <div className="text-[10px] text-slate-500">+{a.minutes}min</div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          {/* When */}
          <section>
            <SectionTitle icon={<Calendar className="w-4 h-4" />}>Preferred date & time</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-3">
              <Labeled label="Date">
                <Input type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} required data-testid="cb-date" />
              </Labeled>
              <Labeled label="Start time">
                <Input type="time" value={form.start_time} onChange={(e) => set({ start_time: e.target.value })} required data-testid="cb-start-time" />
              </Labeled>
            </div>
          </section>

          {/* Notes */}
          <section>
            <SectionTitle>Notes</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-3">
              <Labeled label="Special requests">
                <Textarea rows={3} value={form.special_requests} onChange={(e) => set({ special_requests: e.target.value })} data-testid="cb-special-requests" />
              </Labeled>
              <Labeled label="Access instructions">
                <Textarea rows={3} value={form.access_instructions} onChange={(e) => set({ access_instructions: e.target.value })} data-testid="cb-access" />
              </Labeled>
            </div>
          </section>

          {/* Discount & advanced pricing */}
          <section className="border-t border-slate-200 pt-4">
            <SectionTitle icon={<Sparkles className="w-4 h-4" />}>Discount</SectionTitle>
            <div className="grid sm:grid-cols-4 gap-3">
              <Labeled label="Discount type">
                <Select value={form.discount_type} onValueChange={(v) => set({ discount_type: v })}>
                  <SelectTrigger data-testid="cb-discount-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="code">Discount code</SelectItem>
                    <SelectItem value="percent">Custom percentage</SelectItem>
                    <SelectItem value="fixed">Custom fixed (€)</SelectItem>
                  </SelectContent>
                </Select>
              </Labeled>
              {form.discount_type === "code" && (
                <div className="sm:col-span-3">
                  <Labeled label="Discount code (e.g. NEWTS10)">
                    <Input
                      value={form.discount_code}
                      onChange={(e) => set({ discount_code: e.target.value.toUpperCase() })}
                      placeholder="NEWTS10"
                      data-testid="cb-discount-code"
                    />
                  </Labeled>
                </div>
              )}
              {form.discount_type === "percent" && (
                <Labeled label="Discount %">
                  <Input type="number" min="0" max="100" step="0.5"
                    value={form.discount_pct} onChange={(e) => set({ discount_pct: e.target.value })}
                    placeholder="e.g. 10" data-testid="cb-discount-pct" />
                </Labeled>
              )}
              {form.discount_type === "fixed" && (
                <Labeled label="Discount amount (€ excl. VAT)">
                  <Input type="number" min="0" step="0.5"
                    value={form.discount_amount} onChange={(e) => set({ discount_amount: e.target.value })}
                    placeholder="e.g. 25" data-testid="cb-discount-amount" />
                </Labeled>
              )}
              {form.discount_type !== "none" && (
                <div className={form.discount_type === "code" ? "sm:col-span-4" : "sm:col-span-2"}>
                  <Labeled label="Reason / notes">
                    <Input
                      value={form.discount_reason}
                      onChange={(e) => set({ discount_reason: e.target.value })}
                      placeholder="e.g. Loyal customer · Approved by Hallie"
                      data-testid="cb-discount-reason"
                    />
                  </Labeled>
                </div>
              )}
            </div>

            <details className="mt-4 group">
              <summary className="cursor-pointer text-xs uppercase tracking-wide font-semibold text-slate-500 hover:text-[#1B3A6B]">
                Advanced pricing (overrides)
              </summary>
              <div className="grid sm:grid-cols-4 gap-3 mt-3">
                <Labeled label="Complexity %">
                  <Input type="number" min="0" max="100" step="0.25"
                    value={form.complexity_pct}
                    onChange={(e) => set({ complexity_pct: e.target.value })}
                    placeholder="default 1.25"
                    data-testid="cb-complexity-pct"
                  />
                </Labeled>
                <Labeled label="Hourly rate override (€)">
                  <Input type="number" min="0" step="0.5"
                    value={form.hourly_rate_override}
                    onChange={(e) => set({ hourly_rate_override: e.target.value })}
                    placeholder="for custom-quote services"
                    data-testid="cb-rate-override"
                  />
                </Labeled>
                <Labeled label="Billable hours override">
                  <Input type="number" min="0" step="0.5"
                    value={form.billable_hours_override}
                    onChange={(e) => set({ billable_hours_override: e.target.value })}
                    placeholder="skip duration table"
                    data-testid="cb-hours-override"
                  />
                </Labeled>
                <Labeled label="Fixed quote (€ excl. VAT)">
                  <Input type="number" min="0" step="0.5"
                    value={form.fixed_quote_excl_vat}
                    onChange={(e) => set({ fixed_quote_excl_vat: e.target.value })}
                    placeholder="bypass hours × rate"
                    data-testid="cb-fixed-quote"
                  />
                </Labeled>
              </div>
            </details>

            {/* Price breakdown live */}
            {pricePreview && (
              <div className="mt-4 rounded-lg bg-slate-50 ring-1 ring-slate-200 p-3 text-xs">
                <div className="grid sm:grid-cols-2 gap-1.5">
                  <Row label="Base subtotal" value={`€${pricePreview.base_subtotal_excl_vat?.toFixed(2)}`} />
                  {pricePreview.complexity_fee_excl_vat > 0 && (
                    <Row label={`Complexity ${pricePreview.complexity_pct}%`} value={`+€${pricePreview.complexity_fee_excl_vat?.toFixed(2)}`} />
                  )}
                  {pricePreview.floor_surcharge_excl_vat > 0 && (
                    <Row label={`Floor surcharge ${pricePreview.floor_surcharge_pct}%`} value={`+€${pricePreview.floor_surcharge_excl_vat?.toFixed(2)}`} />
                  )}
                  {pricePreview.discount_amount > 0 && (
                    <Row label={`Discount ${pricePreview.discount?.code || pricePreview.discount?.type || ""}`} value={`−€${pricePreview.discount_amount?.toFixed(2)}`} highlight />
                  )}
                  <Row label="Discounted subtotal" value={`€${pricePreview.discounted_subtotal_excl_vat?.toFixed(2)}`} bold />
                  <Row label={`VAT ${pricePreview.vat_pct}%`} value={`€${pricePreview.vat_amount?.toFixed(2)}`} />
                  <Row label="Customer total" value={`€${pricePreview.total?.toFixed(2)}`} bold />
                  <Row label="Owner share (admin)" value={`€${pricePreview.owner_share?.toFixed(2)}`} muted />
                  <Row label="Hallie share (admin)" value={`€${pricePreview.hallie_share?.toFixed(2)}`} muted negative={pricePreview.hallie_share < 0} />
                </div>
                {(pricePreview.warnings || []).length > 0 && (
                  <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded p-2">
                    {pricePreview.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Custom-quote warning was shown above the date section */}

          <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-2 sm:justify-between">
            <Button type="button" variant="ghost" onClick={onClose} data-testid="cb-cancel">Cancel</Button>
            {isEditDraft ? (
              <Button
                type="submit"
                disabled={submitting || isCustomQuote}
                onClick={() => setSubmitMode("draft")}
                className="bg-[#1B3A6B] hover:bg-[#152D54] text-white"
                data-testid="cb-submit"
              >
                {submitting ? "Saving…" : "Save changes"}
              </Button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  type="submit"
                  variant="outline"
                  disabled={submitting || isCustomQuote}
                  onClick={() => setSubmitMode("quote")}
                  data-testid="cb-send-quote"
                  className="border-[#1B3A6B] text-[#1B3A6B] hover:bg-[#1B3A6B]/5"
                >
                  <Send className="w-4 h-4 mr-1" />
                  {submitting && submitMode === "quote" ? "Sending…" : "Send quote"}
                </Button>
                <Button
                  type="submit"
                  variant="secondary"
                  disabled={submitting || isCustomQuote}
                  onClick={() => setSubmitMode("draft")}
                  data-testid="cb-save-draft"
                >
                  <FileDown className="w-4 h-4 mr-1" />
                  {submitting && submitMode === "draft" ? "Saving…" : "Save draft"}
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || isCustomQuote}
                  onClick={() => setSubmitMode("confirm")}
                  data-testid="cb-confirm-booking"
                  className="bg-[#5BA83F] hover:bg-[#4A8A33] text-white"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  {submitting && submitMode === "confirm" ? "Confirming…" : "Confirm booking"}
                </Button>
              </div>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SectionTitle({ icon, children }) {
  return (
    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">
      {icon}{children}
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value, bold, highlight, muted, negative }) {
  const valueClass = [
    bold ? "font-bold text-[#1B3A6B]" : "",
    highlight ? "text-emerald-700 font-semibold" : "",
    muted ? "text-slate-500" : "",
    negative ? "text-rose-600 font-bold" : "",
  ].join(" ");
  return (
    <div className="flex justify-between">
      <span className={muted ? "text-slate-500" : "text-slate-600"}>{label}</span>
      <span className={valueClass}>{value}</span>
    </div>
  );
}

function formatDuration(hours) {
  const h = Number(hours) || 0;
  if (!h) return "—";
  const whole = Math.floor(h);
  const min = Math.round((h - whole) * 60);
  if (whole === 0) return `${min}min`;
  if (min === 0) return `${whole}h`;
  return `${whole}h ${min}min`;
}

function RescheduleBookingDialog({ booking, onClose, onDone }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [reason, setReason] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (booking) {
      setDate(booking.date || "");
      setTime(booking.start_time || "");
      setReason("");
    }
  }, [booking?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!booking) return null;
  const changed = date !== booking.date || time !== booking.start_time;

  const submit = async () => {
    if (!date || !time) return;
    setWorking(true);
    try {
      await api.post(`/admin/bookings/${booking.id}/reschedule`, {
        new_date: date, new_start_time: time, reason,
      });
      toast.success(`Booking rescheduled to ${date} at ${time}.`);
      onDone?.();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#5BA83F]" /> Reschedule booking
          </DialogTitle>
          <DialogDescription>
            <b>{booking.customer_name}</b> · currently <b>{booking.date}</b> at <b>{booking.start_time}</b>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Labeled label="New date">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="resch-date" />
            </Labeled>
            <Labeled label="New start time">
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="resch-time" />
            </Labeled>
          </div>
          <Labeled label="Reason / internal note (optional)">
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="resch-reason" placeholder="Why is it being moved?" />
          </Labeled>
          <div className="rounded-md bg-blue-50 ring-1 ring-blue-200 p-3 text-xs text-blue-900">
            The linked job in the schedule is also updated. No cancellation fee is charged to the customer.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={working} data-testid="resch-cancel">Cancel</Button>
          <Button onClick={submit} disabled={working || !changed} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white" data-testid="resch-confirm">
            {working ? "Saving…" : "Reschedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdjustPriceDialog({ booking, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (booking) {
      setAmount(String(booking.amount ?? ""));
      setReason("");
    }
  }, [booking?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!booking) return null;
  const newAmount = Number(amount) || 0;
  const diff = newAmount - Number(booking.amount || 0);
  const disabled = working || !reason.trim() || newAmount < 0 || Math.abs(diff) < 0.005;

  const submit = async () => {
    setWorking(true);
    try {
      await api.post(`/admin/bookings/${booking.id}/adjust-price`, {
        new_amount: newAmount, reason: reason.trim(), keep_vat_pct: true,
      });
      toast.success(`Price adjusted to €${newAmount.toFixed(2)}.`);
      onDone?.();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="adjust-price-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-[#5BA83F]" /> Adjust customer price
          </DialogTitle>
          <DialogDescription>
            <b>{booking.customer_name}</b> · current total <b>€{Number(booking.amount || 0).toFixed(2)}</b> incl. VAT
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Labeled label="New total (€ incl. VAT)">
            <Input type="number" min="0" step="0.5" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="adj-amount" />
          </Labeled>
          {diff !== 0 && Math.abs(diff) >= 0.005 && (
            <div className={`text-xs ${diff > 0 ? "text-emerald-700" : "text-rose-600"}`}>
              {diff > 0 ? "+" : ""}€{diff.toFixed(2)} vs. original
            </div>
          )}
          <Labeled label="Reason (required — kept in audit trail)">
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adj-reason" placeholder="e.g. Extra 1h approved by customer / Discount applied post-booking" />
          </Labeled>
          <div className="rounded-md bg-amber-50 ring-1 ring-amber-200 p-3 text-xs text-amber-900">
            VAT % is preserved; the subtotal and revenue split are recomputed. Owner share stays €30 × billable hours; Hallie share is the remainder.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={working} data-testid="adj-cancel">Cancel</Button>
          <Button onClick={submit} disabled={disabled} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white" data-testid="adj-confirm">
            {working ? "Saving…" : "Save adjustment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CANCEL_REASONS = ["Duplicate Booking", "Change of Plans", "Found Someone Else", "Other"];

function CancelBookingDialog({ booking, working, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [other, setOther] = useState("");

  useEffect(() => {
    if (booking) { setReason(""); setOther(""); }
  }, [booking?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!booking) return null;
  const isOther = reason === "Other";
  const disabled = working || !reason || (isOther && !other.trim());

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="cancel-reason-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <XCircle className="w-5 h-5" /> Cancel booking
          </DialogTitle>
          <DialogDescription>
            <b>{booking.customer_name}</b> · {booking.service_type} · {booking.date}
            {booking.invoice_no && <> · Invoice <b>{booking.invoice_no}</b></>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Labeled label="Reason for cancellation">
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="cancel-reason-select"><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent>
                {CANCEL_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </Labeled>

          {isOther && (
            <Labeled label="Please specify">
              <Textarea
                rows={3}
                value={other}
                onChange={(e) => setOther(e.target.value)}
                placeholder="Tell us briefly why this booking is being cancelled…"
                data-testid="cancel-reason-other"
              />
            </Labeled>
          )}

          <div className="rounded-md bg-amber-50 ring-1 ring-amber-200 p-3 text-xs text-amber-900">
            This will mark the booking as <b>Cancelled</b>, hide it from the active list, and cancel the linked job in the schedule. The invoice PDF stays available for records.
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={working} data-testid="cancel-reason-back">Keep booking</Button>
          <Button
            disabled={disabled}
            onClick={() => onConfirm?.(reason, isOther ? other.trim() : "")}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="cancel-reason-confirm"
          >
            {working ? "Cancelling…" : "Cancel booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
